import React, { useState } from 'react';
import { FlagIcon, MessageSquareIcon } from 'lucide-react';
import { bundleRows } from '../../data/harrow';
import { useAssessment } from '../../contexts/AssessmentContext';
import { FlagDrawer } from './FlagDrawer';

// LAW-001 bundle review. No verdict column, no status column, no highlighting —
// the instrument records what the documents say and takes no view.
export function BundleTable() {
  const {
    flags,
    saveFlag,
    removeFlag,
    locked,
    timeUp
  } = useAssessment();
  const [drawer, setDrawer] = useState<{
    rowId: string;
    label: string;
    kind: 'flag' | 'correction';
  } | null>(null);
  const readOnly = locked || timeUp;
  return <div className="flex h-full flex-col" data-id="element-192" data-mp="27vgo3llvxl19">
      <div className="border-b border-slate-200 bg-white px-5 py-3" data-id="element-193" data-mp="22u0xzf4uooc0">
        <h2 className="text-sm font-semibold text-slate-900" data-id="element-194" data-mp="2gu21718l87un">
          Bundle review — Project Harrow, against the deal instructions
        </h2>
        <p className="mt-0.5 text-xs text-slate-500" data-id="element-195" data-mp="2ctru1l927be7">
          Documented positions are reproduced as they appear in the bundle.
        </p>
      </div>

      <div className="flex-1 overflow-auto" data-id="element-196" data-mp="1a20yiap7r31m">
        <table className="w-full border-collapse text-sm" data-id="element-197" data-mp="1a2sieo9g7cc2">
          <thead className="sticky top-0 z-10 bg-slate-100 text-left" data-id="element-198" data-mp="1f1dgah189jxc">
            <tr className="text-[11px] uppercase tracking-wider text-slate-600" data-id="element-199" data-mp="1f36npyjo086e">
              <th className="border-b border-slate-200 px-4 py-2.5 font-semibold" data-id="element-200" data-mp="1k1i12j9650td">Ref</th>
              <th className="border-b border-slate-200 px-4 py-2.5 font-semibold" data-id="element-201" data-mp="1oyrxwjyw0er8">Term</th>
              <th className="border-b border-slate-200 px-4 py-2.5 font-semibold" data-id="element-202" data-mp="1szk75d7rejni">As instructed</th>
              <th className="border-b border-slate-200 px-4 py-2.5 font-semibold" data-id="element-203" data-mp="1xswi4kjimrxs">Source</th>
              <th className="border-b border-slate-200 px-4 py-2.5 font-semibold" data-id="element-204" data-mp="22pn1phuj45ny">As documented</th>
              <th className="border-b border-slate-200 px-4 py-2.5 font-semibold" data-id="element-205" data-mp="27r2kt4d5c23p">Actions</th>
            </tr>
          </thead>
          <tbody data-id="element-206" data-mp="2cp467vy1odic">
            {bundleRows.map(row => {
            const entry = flags[row.id];
            const label = row.term;
            return <React.Fragment key={row.id} data-id="element-207" data-mp="1i2ossg2srif9">
                  <tr className="border-b border-slate-100 bg-white align-top transition-colors hover:bg-slate-50" data-id="element-208" data-mp="1hzsswm7e2jo6">
                    <td className="whitespace-nowrap px-4 py-3 font-mono-num text-xs text-slate-500" data-id="element-209" data-mp="1rztdsckwp7uh">
                      {row.id}
                    </td>
                    <td className="px-4 py-3 text-slate-900" data-id="element-210" data-mp="25pn83af6kkbl">{row.term}</td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-211" data-mp="20r3jf297cufj">{row.instructed}</td>
                    <td className="px-4 py-3 text-xs text-slate-500" data-id="element-212" data-mp="2fpxgmhq9t3kf">{row.source}</td>
                    <td className="px-4 py-3 text-slate-800" data-id="element-213" data-mp="2aqkmrd0zew8c">{row.documented}</td>
                    <td className="whitespace-nowrap px-4 py-3" data-id="element-214" data-mp="2aoggwpjivn5f">
                      <div className="flex items-center gap-1.5" data-id="element-215" data-mp="lbwk43i70sni">
                        <button type="button" disabled={readOnly} onClick={() => setDrawer({
                      rowId: row.id,
                      label,
                      kind: 'flag'
                    })} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:opacity-40" data-id="element-216" data-mp="gd1tifdzhzsu">
                          <FlagIcon className="h-3 w-3" data-id="element-217" data-mp="299dtu04hdtu4" />
                          Flag
                        </button>
                        <button type="button" disabled={readOnly} onClick={() => setDrawer({
                      rowId: row.id,
                      label,
                      kind: 'correction'
                    })} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:opacity-40" data-id="element-218" data-mp="2dazei7n9bcev">
                          <MessageSquareIcon className="h-3 w-3" data-id="element-219" data-mp="1ylkhegagdtvd" />
                          Query
                        </button>
                        {entry && <span className="rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-800" data-id="element-220" data-mp="1tkmz83pg0ztv">
                            {entry.kind === 'correction' ? 'queried' : 'flagged'}
                          </span>}
                      </div>
                    </td>
                  </tr>
                  {entry && <tr className="bg-teal-50/40" data-id="element-221" data-mp="1aux93twml28a">
                      <td colSpan={6} className="border-b border-slate-100 px-4 py-2 text-xs text-teal-900" data-id="element-222" data-mp="15wn8v4w1nbft">
                        <span className="font-semibold" data-id="element-223" data-mp="11wcxfcphfstk">Your entry: </span>
                        {entry.what}
                        <span className="ml-2 text-teal-700/70" data-id="element-224" data-mp="wv5nb6zvahjq">
                          · {entry.actionType} · sources {entry.sources.join(', ') || 'none cited'}
                        </span>
                      </td>
                    </tr>}
                </React.Fragment>;
          })}
          </tbody>
        </table>
      </div>

      {drawer && <FlagDrawer rowId={drawer.rowId} rowLabel={drawer.label} kind={drawer.kind} existing={flags[drawer.rowId]} onSave={saveFlag} onDelete={() => removeFlag(drawer.rowId)} onClose={() => setDrawer(null)} data-id="element-225" data-mp="3djyi61rafw17" />}
    </div>;
}